// ============================================================
//  ★ 데이터 SEAM — 개발자 작업은 "여기"만 ★
// ------------------------------------------------------------
//  변환 산출물의 유일한 데이터 접점. fn 내부를 2.0 API 호출로 교체했다.
//  BE: GET /api/v1/global/invoice-monitorings (LOGISPRD-9600, UI_GLOBAL_INVOICE_MONITOR ⋈ UI_PAIRORDER)
//  - 필터 시맨틱(1.0 보존): shipperCode eq / invoiceNos IN / status(분류) eq / closingDate 범위 / shipperOrderNos IN
//  - 주문번호 필터는 1.0 alias 버그로 SQL 오류였던 것의 의도 복원 (LLD-9600 §6 결함 #1)
//  순수 변환(응답 매핑/저장 payload)은 mapping.ts 에 분리 — request 비의존 모듈로 테스트한다.
// ============================================================
import request from '@/utils/request'
import { Utils } from '@/utils/Utils'
import { useFetchAllRows, type FetchedChunk, type UseFetchAllRowsResult } from '@/hooks/useFetchAllRows'
import { GL11_FETCH_CHUNK_SIZE, GL11_URL_LENGTH_THRESHOLD } from '../components/consts'
import { dedupeById, toMonitoringQueryError, toMonitoringRow, toUpdateItem } from './mapping'
import type { GlobalMonitoringParams, GlobalMonitoringRow, MonitoringItem } from './mapping'

export type { GlobalMonitoringParams, GlobalMonitoringRow } from './mapping'


const stableParams = (params: GlobalMonitoringParams): GlobalMonitoringParams => {
  const { _seq: _ignored, ...stable } = params as GlobalMonitoringParams & { _seq?: number }
  return stable
}

/** 맨 앞은 메뉴코드(탭 닫기 캐시 삭제). 페이지는 받은 행 안의 보기 상태라 키에 없다. */
export const globalMonitoringQueryKeys = {
  all: ['gl13', 'getGlobalMonitoringList'] as const,
  list: (params: GlobalMonitoringParams) => [...globalMonitoringQueryKeys.all, stableParams(params)] as const,
}

const GLOBAL_MONITORING_LIST_URL = '/api/v1/global/invoice-monitorings'

// 구간 조회 — BE 상한(GL11_FETCH_CHUNK_SIZE) 구간으로 끝까지 받는다(LOGISPRD-15271). page 는 BE 0-based 구간 번호다.
const requestParams = (params: GlobalMonitoringParams, page: number) => ({
  shipperCode: params.shipperCode || undefined,
  invoiceNos: params.invoiceNos?.length ? params.invoiceNos : undefined,
  status: params.status || undefined,
  closingDateFrom: params.closingDateFrom || undefined,
  closingDateTo: params.closingDateTo || undefined,
  shipperOrderNos: params.shipperOrderNos?.length ? params.shipperOrderNos : undefined,
  page,
  size: GL11_FETCH_CHUNK_SIZE,
})

const shouldUsePost = (body: ReturnType<typeof requestParams>): boolean => {
  const legacyUrl = `${GLOBAL_MONITORING_LIST_URL}${Utils.qsStringify(body)}`
  return legacyUrl.length > GL11_URL_LENGTH_THRESHOLD
}

export const fetchGlobalMonitoringList = async (params: GlobalMonitoringParams, page: number): Promise<FetchedChunk<GlobalMonitoringRow>> => {
  try {
    const body = requestParams(params, page)
    const get = () =>
      request.get<ApiResponseV2<{ items: MonitoringItem[]; pageInfo?: { totalCount: number } }>, Record<string, unknown>>(
        GLOBAL_MONITORING_LIST_URL,
        body,
      )
    let res
    if (shouldUsePost(body)) {
      try {
        res = await request.post<ApiResponseV2<{ items: MonitoringItem[]; pageInfo?: { totalCount: number } }>, Record<string, unknown>>(
          '/api/v1/global/invoice-monitorings/search',
          body,
        )
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status
        if (status !== 404 && status !== 405) throw error
        res = await get()
      }
    } else {
      res = await get()
    }
    const items = (res.data?.items ?? []).map(toMonitoringRow)
    return { items, totalCount: res.data?.pageInfo?.totalCount ?? items.length }
  } catch (error) {
    throw toMonitoringQueryError(error)
  }
}

/** 행 키 — 헤드 1건이 합포 주문라인 수만큼 펼쳐져 _id 만으로는 겹친다. 주문번호+옵션번호까지 더하면 유일하다(UI_PAIRORDER 유니크 SPR_NM·SPR_ORD_NO·SPR_ORD_OPT_NO). */
const getKey = (r: GlobalMonitoringRow) => `${r._id}|${r.SPR_ORD_NO}|${r.SPR_ORD_OPT_NO}`

/** 전량 조회 — 실패 안내는 훅이 전역 알럿으로 한 번 낸다(시간 초과는 toMonitoringQueryError 가 기간 축소 문구로 바꾼다). */
export const useGetGlobalMonitoringList = (params: GlobalMonitoringParams, enabled = true): UseFetchAllRowsResult<GlobalMonitoringRow> =>
  useFetchAllRows({
    queryKey: globalMonitoringQueryKeys.list(params),
    fetchPage: (page) => fetchGlobalMonitoringList(params, page),
    chunkSize: GL11_FETCH_CHUNK_SIZE,
    getKey,
    enabled,
  })

// 저장 — PUT /api/v1/global/invoice-monitorings (LOGISPRD-9601, 1.0 GL11_UpdateForm 파리티)
// CS 편집 화이트리스트 11필드 전체 치환. 분류(status)는 '기타'일 때만 서버가 반영한다.
// 합포 팬아웃으로 같은 _id 가 여러 행일 수 있어 호출 전 _id 로 dedup 한다 (서버는 중복 id 400).
export const updateGlobalMonitorings = async (rows: GlobalMonitoringRow[]): Promise<number[]> => {
  const res = await request.put<ApiResponseV2<{ items: number[] }>, { rows: Record<string, unknown>[] }>('/api/v1/global/invoice-monitorings', {
    rows: dedupeById(rows).map(toUpdateItem),
  })
  return res.data?.items ?? []
}
